import {
  Avatar,
  Button,
  Group,
  IconButton,
  Input,
  Modal,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@assetwatch/ui";
import { WorkRole } from "@assetwatch/work-authorization";
import { useEffect, useState } from "react";
import { useWorkScope } from "../../../components/WorkScopeProvider";
import { todayInTimeZone } from "../../../lib/facilityCalendarDate";
import type { WorkOrderId } from "../../../lib/workIds";
import {
  type CreateWorkOrderTimeEntryInput,
  useWorkOrderTimeEntryMutations,
  type WorkOrderTimeEntryInput,
} from "../../../mutations/useWorkOrderTimeEntryMutations";
import { useTeamsUsersQuery } from "../../../queries/useTeamsUsersQuery";
import {
  useWorkOrderTimeEntriesQuery,
  type WorkOrderTimeEntry,
} from "../../../queries/useWorkOrderTimeEntriesQuery";
import { DetailCard } from "./WorkOrderDetailLayout";

const CONTRIBUTOR_ROLE_NAMES = new Set(["Technician", "Administrator"]);

function formatDuration(durationMinutes: number) {
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

function formatWorkDate(workDate: string) {
  const date = new Date(`${workDate}T00:00:00.000Z`);
  if (Number.isNaN(date.valueOf())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(date);
}

// New entries default to the Facility's today, which is the latest date the
// server accepts, rather than the browser's local date.
function emptyDraft(today: string): CreateWorkOrderTimeEntryInput {
  return {
    workDate: today,
    durationMinutes: 0,
    note: "",
  };
}

function draftFromEntry(entry: WorkOrderTimeEntry): WorkOrderTimeEntryInput {
  return {
    workDate: entry.workDate,
    durationMinutes: entry.durationMinutes,
    note: entry.note ?? "",
  };
}

// Only the Contributor changes how the editor lays out: administrators pick
// one when adding time, and see it read-only when editing.
type ContributorControl =
  | {
      kind: "picker";
      options: { label: string; value: string }[];
      selectedUserId: number | null;
      onSelect: (userId: number) => void;
    }
  | { kind: "readonly"; initials: string; name: string };

type DurationPart = { value?: number; error?: string };

/**
 * Parses the raw text of the Hours or Minutes field. Decimals are rejected
 * rather than rounded, so the error is decided from the string, not from the
 * number it would convert to.
 */
function parseDurationPart(
  text: string,
  label: "Hours" | "Minutes",
  max?: number,
): DurationPart {
  const trimmed = text.trim();
  if (trimmed === "") return { value: 0 };
  if (trimmed.startsWith("-")) {
    return { error: `${label} cannot be negative.` };
  }
  if (!/^\d+$/.test(trimmed)) {
    return { error: `${label} must be a whole number.` };
  }
  const value = Number(trimmed);
  if (max !== undefined && value > max) {
    return { error: `${label} must be ${max} or less.` };
  }
  return { value };
}

function TimeEntryEditor({
  contributor,
  draft,
  isPending,
  onCancel,
  onChange,
  onSave,
  title,
  today,
}: {
  contributor?: ContributorControl;
  draft: CreateWorkOrderTimeEntryInput;
  isPending: boolean;
  onCancel: () => void;
  onChange: (draft: CreateWorkOrderTimeEntryInput) => void;
  onSave: () => void;
  title: string;
  today: string;
}) {
  // Duration errors appear once the user has edited Hours or Minutes, so a new
  // entry does not open already flagged.
  const [durationTouched, setDurationTouched] = useState(false);
  // The raw text is kept so a decimal can be detected before Number() hides it.
  const [hoursText, setHoursText] = useState(
    String(Math.floor(draft.durationMinutes / 60)),
  );
  const [minutesText, setMinutesText] = useState(
    String(draft.durationMinutes % 60),
  );
  const hoursResult = parseDurationPart(hoursText, "Hours");
  const minutesResult = parseDurationPart(minutesText, "Minutes", 59);
  const hoursError = hoursResult.error;
  const minutesError = minutesResult.error;
  const hours = hoursResult.value ?? 0;
  const minutes = minutesResult.value ?? 0;
  const hasPartError = hoursError !== undefined || minutesError !== undefined;
  const invalidDuration = hasPartError || hours * 60 + minutes <= 0;
  // ISO dates compare correctly as strings, so a future date is any value
  // after the Facility's today.
  const invalidDate =
    !/^\d{4}-\d{2}-\d{2}$/.test(draft.workDate) || draft.workDate > today;
  const zeroDuration =
    durationTouched && !hasPartError && hours * 60 + minutes <= 0;
  return (
    <div className="border-b border-outline-subtle bg-surface-subtle px-(--spacing-20) py-(--spacing-16)">
      <Stack gap={12}>
        <Text weight="medium">{title}</Text>
        <div
          className={`grid grid-cols-1 gap-(--spacing-12) ${contributor ? "sm:grid-cols-5" : "sm:grid-cols-4"}`}
        >
          {contributor?.kind === "picker" ? (
            /* @ds-deviation: the POC add row (WoTimeEntries in poc-design wo-detail.jsx) shows only the acting user's avatar and name with no picker, but the story requires an Administrator to add time for a selected contributor — product decision, promote the picker into the POC and design system */
            <Select
              label="Contributor"
              searchable
              onChange={(value) => {
                if (value) contributor.onSelect(Number(value));
              }}
              options={contributor.options}
              placeholder="Choose a contributor"
              size="sm"
              value={
                contributor.selectedUserId === null
                  ? null
                  : String(contributor.selectedUserId)
              }
            />
          ) : contributor?.kind === "readonly" ? (
            <Stack gap={4}>
              <Text size="sm" weight="medium">
                Contributor
              </Text>
              <Group gap={8}>
                <Avatar
                  initials={contributor.initials}
                  size="xs"
                  variant="internal"
                />
                <Text>{contributor.name}</Text>
              </Group>
            </Stack>
          ) : null}
          <Input
            error={invalidDate ? "Enter a date on or before today" : undefined}
            label="Date"
            max={today}
            onChange={(event) =>
              onChange({ ...draft, workDate: event.target.value })
            }
            required
            size="sm"
            type="date"
            value={draft.workDate}
          />
          <Input
            error={
              hoursError ??
              (zeroDuration ? "Enter a duration greater than zero" : undefined)
            }
            label="Hours"
            min={0}
            onChange={(event) => {
              setDurationTouched(true);
              const text = event.target.value;
              setHoursText(text);
              const result = parseDurationPart(text, "Hours");
              if (result.value !== undefined && minutesError === undefined) {
                onChange({
                  ...draft,
                  durationMinutes: result.value * 60 + minutes,
                });
              }
            }}
            size="sm"
            step={1}
            type="number"
            value={hoursText}
          />
          <Input
            error={minutesError}
            label="Minutes"
            max={59}
            min={0}
            onChange={(event) => {
              setDurationTouched(true);
              const text = event.target.value;
              setMinutesText(text);
              const result = parseDurationPart(text, "Minutes", 59);
              if (result.value !== undefined && hoursError === undefined) {
                onChange({
                  ...draft,
                  durationMinutes: hours * 60 + result.value,
                });
              }
            }}
            size="sm"
            step={1}
            type="number"
            value={minutesText}
          />
          <Input
            label="Note"
            maxLength={500}
            onChange={(event) =>
              onChange({ ...draft, note: event.target.value })
            }
            size="sm"
            value={draft.note ?? ""}
          />
        </div>
        <Group justify="end">
          <Button
            disabled={isPending}
            onClick={onCancel}
            size="sm"
            variant="ghost"
          >
            Cancel
          </Button>
          <Button
            disabled={invalidDate || invalidDuration || isPending}
            loading={isPending}
            loadingText="Saving"
            onClick={onSave}
            size="sm"
          >
            Save
          </Button>
        </Group>
      </Stack>
    </div>
  );
}

export function WorkOrderTimeAndCostsSection({
  workOrderId,
  canEditWorkOrder,
  canAddTimeToCompleted = false,
  canAddTimeForCompletionReview = false,
  completionReview = false,
  enabled = true,
  onPendingChange,
  onReviewReadyChange,
}: {
  workOrderId: WorkOrderId;
  canEditWorkOrder: boolean;
  /**
   * The Work Order is Done and the caller may add time to it without
   * reopening it (the same people who may reopen it).
   */
  canAddTimeToCompleted?: boolean;
  /** The caller may add time while reviewing a Work Order they can complete. */
  canAddTimeForCompletionReview?: boolean;
  /** Shows the complete posted list and suppresses edits during completion review. */
  completionReview?: boolean;
  enabled?: boolean;
  onPendingChange?: (hasPendingChanges: boolean) => void;
  onReviewReadyChange?: (isReady: boolean) => void;
}) {
  const { selectedFacility } = useWorkScope();
  const query = useWorkOrderTimeEntriesQuery(workOrderId, {
    completionReview,
    enabled,
  });
  const mutations = useWorkOrderTimeEntryMutations(workOrderId);
  const teamsUsers = useTeamsUsersQuery();
  const currentUserId = teamsUsers.data?.currentUserId ?? null;
  const isAdministrator = selectedFacility?.role === WorkRole.ADMINISTRATOR;
  const isTechnician = selectedFacility?.role === WorkRole.TECHNICIAN;
  const canManageTime = isAdministrator || (isTechnician && canEditWorkOrder);
  // A Technician on a Done Work Order may add time but not change or remove
  // existing entries, so this gates only the Add time control.
  const canAddTime =
    canManageTime ||
    (isTechnician &&
      ((completionReview && canAddTimeForCompletionReview) ||
        canAddTimeToCompleted));
  // The server already restricts non-administrators to their own entries;
  // this keeps Edit and Delete off any row that is not the caller's.
  const canManageEntry = (entry: WorkOrderTimeEntry) =>
    canManageTime &&
    (isAdministrator ||
      (currentUserId !== null && entry.user?.id === currentUserId));
  // Only people who can carry out work are offered, and disabled accounts are
  // left out; the API enforces the same Roles.
  const contributorOptions = (teamsUsers.data?.users ?? [])
    .filter(
      (user) =>
        user.status !== "Disabled" &&
        user.role !== null &&
        CONTRIBUTOR_ROLE_NAMES.has(user.role),
    )
    .map((user) => ({ label: user.name, value: String(user.id) }));
  const today = todayInTimeZone(selectedFacility?.timezone);
  const entries = query.data?.timeEntries ?? [];
  const totalLabor = formatDuration(query.data?.totalDurationMinutes ?? 0);
  const [editor, setEditor] = useState<{
    entryId: string | null;
    draft: CreateWorkOrderTimeEntryInput;
  } | null>(null);
  const [deleteEntry, setDeleteEntry] = useState<WorkOrderTimeEntry | null>(
    null,
  );
  const pending = mutations.create.isPending || mutations.update.isPending;
  const hasPendingChanges = editor !== null || pending;
  const isReviewReady = !query.isPending && !query.isError;
  useEffect(() => {
    onPendingChange?.(hasPendingChanges);
  }, [hasPendingChanges, onPendingChange]);
  useEffect(() => {
    onReviewReadyChange?.(isReviewReady);
  }, [isReviewReady, onReviewReadyChange]);
  const save = () => {
    if (!editor) return;
    if (editor.entryId) {
      mutations.update.mutate(
        { id: editor.entryId, ...editor.draft },
        { onSuccess: () => setEditor(null) },
      );
    } else {
      mutations.create.mutate(editor.draft, {
        onSuccess: () => setEditor(null),
      });
    }
  };

  return (
    <DetailCard
      id="time-costs"
      title="Time entries"
      actions={
        <Group gap={12}>
          {/* @ds-deviation: the ordinary time tab follows the 2026-10-07 own-rows product rule; the completion review uses the later IWA-20859 rule to show all rows to an authorized completer. */}
          <span className="tabular-nums">
            <Text size="sm" tone="subtle">
              {query.isPending
                ? "Loading"
                : isAdministrator || completionReview
                  ? `${entries.length} ${entries.length === 1 ? "entry" : "entries"} · ${totalLabor} labor logged`
                  : `Your entries: ${entries.length} · ${totalLabor} labor logged on this Work Order`}
            </Text>
          </span>
          {canAddTime ? (
            <Button
              disabled={editor !== null}
              icon="add-plus"
              onClick={() =>
                setEditor({ entryId: null, draft: emptyDraft(today) })
              }
              size="sm"
              variant="outline"
            >
              Add time
            </Button>
          ) : null}
        </Group>
      }
    >
      {editor?.entryId === null ? (
        <TimeEntryEditor
          contributor={
            isAdministrator
              ? {
                  kind: "picker",
                  options: contributorOptions,
                  selectedUserId: editor.draft.userId ?? currentUserId,
                  // Choosing yourself is the default, so it is not sent.
                  onSelect: (userId) =>
                    setEditor({
                      ...editor,
                      draft: {
                        ...editor.draft,
                        userId: userId === currentUserId ? undefined : userId,
                      },
                    }),
                }
              : undefined
          }
          draft={editor.draft}
          isPending={pending}
          onCancel={() => setEditor(null)}
          onChange={(draft) => setEditor({ ...editor, draft })}
          onSave={save}
          title="Add time"
          today={today}
        />
      ) : null}
      {query.isPending ? (
        <div className="px-(--spacing-20) py-(--spacing-20)">
          <Text tone="subtle">Loading time entries…</Text>
        </div>
      ) : query.isError ? (
        <div className="px-(--spacing-20) py-(--spacing-20)">
          <Stack gap={8}>
            <Text tone="negative">Unable to load time entries.</Text>
            <Button
              onClick={() => void query.refetch()}
              size="sm"
              variant="outline"
            >
              Retry
            </Button>
          </Stack>
        </div>
      ) : entries.length === 0 ? (
        <div className="px-(--spacing-20) py-(--spacing-20)">
          <Text tone="subtle">
            {isAdministrator
              ? "No time entries have been recorded."
              : "You have not recorded any time on this Work Order."}
          </Text>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table aria-label="Work Order time entries" size="sm">
            <TableHeader>
              <TableRow section="header">
                <TableHead>User</TableHead>
                <TableHead align="right">Duration</TableHead>
                <TableHead>Date</TableHead>
                <TableHead aria-label="Actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) =>
                editor?.entryId === entry.id ? (
                  <TableRow key={entry.id}>
                    <TableCell colSpan={4}>
                      <TimeEntryEditor
                        contributor={
                          isAdministrator
                            ? {
                                kind: "readonly",
                                initials: entry.user?.initials ?? "YO",
                                name: entry.user?.name ?? "You",
                              }
                            : undefined
                        }
                        draft={editor.draft}
                        isPending={pending}
                        onCancel={() => setEditor(null)}
                        onChange={(draft) => setEditor({ ...editor, draft })}
                        onSave={save}
                        title="Edit time"
                        today={today}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={entry.id}>
                    <TableCell>
                      <Group gap={8}>
                        <Avatar
                          initials={entry.user?.initials ?? "YO"}
                          size="xs"
                          variant="internal"
                        />
                        <Text>{entry.user?.name ?? "You"}</Text>
                      </Group>
                    </TableCell>
                    <TableCell align="right" variant="text">
                      <span className="tabular-nums">
                        {formatDuration(entry.durationMinutes)}
                      </span>
                    </TableCell>
                    <TableCell>{formatWorkDate(entry.workDate)}</TableCell>
                    <TableCell>
                      {!completionReview && canManageEntry(entry) ? (
                        <Group gap={4} justify="end">
                          <IconButton
                            aria-label={`Edit time entry from ${formatWorkDate(entry.workDate)}`}
                            icon="edit-pen"
                            onClick={() =>
                              setEditor({
                                entryId: entry.id,
                                draft: draftFromEntry(entry),
                              })
                            }
                            size="sm"
                            variant="ghost"
                          />
                          <IconButton
                            aria-label={`Delete time entry from ${formatWorkDate(entry.workDate)}`}
                            icon="trash-delete-bin"
                            onClick={() => setDeleteEntry(entry)}
                            size="sm"
                            variant="ghost"
                          />
                        </Group>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </div>
      )}
      <Modal
        dismissible={!mutations.remove.isPending}
        footer={
          <Group justify="end">
            <Button
              disabled={mutations.remove.isPending}
              onClick={() => setDeleteEntry(null)}
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              intent="destructive"
              loading={mutations.remove.isPending}
              loadingText="Deleting"
              onClick={() => {
                if (deleteEntry)
                  mutations.remove.mutate(deleteEntry.id, {
                    onSuccess: () => setDeleteEntry(null),
                  });
              }}
            >
              Delete
            </Button>
          </Group>
        }
        onOpenChange={(open) => {
          if (!open && !mutations.remove.isPending) setDeleteEntry(null);
        }}
        open={deleteEntry !== null}
        title="Delete time entry?"
      >
        <Text>
          This removes{" "}
          {deleteEntry ? formatDuration(deleteEntry.durationMinutes) : "this"}{" "}
          of logged time from the Work Order.
        </Text>
      </Modal>
    </DetailCard>
  );
}
